import React, { useMemo } from 'react';
import { ErrorLabel, SuccessLabel } from '../common';
import { dateRange } from '../common/filters/customFilters';
import { TableColumn } from '../common/table';
import { makeOpenModalColumn } from '../common/table/OpenModalColumn';
import { makeSelectColumn } from '../common/table/SelectColumn';
import { AmountCell, BooleanCell, DateCell } from '../common/table/TableComponents';
import { DownloadCell } from './DownloadCell';
import { InvoiceAmountCell } from './InvoiceAmountCell';
import { PONumberModalCell } from './PONumberModalCell';
import { BillingTableItem } from './types';

export const invoiceIsPaid = ({ Balance }: BillingTableItem) => Balance <= 0;
export const invoiceIsUnPaid = (invoice: BillingTableItem) => !invoiceIsPaid(invoice);

const PaidStatus = <SuccessLabel>Paid</SuccessLabel>;
const UnpaidStatus = <ErrorLabel>Unpaid</ErrorLabel>;

export type ColumnIds =
  | 'Status'
  | 'InvoiceNumber'
  | 'InvoiceDate'
  | 'DueDate'
  | 'Amount'
  | 'Balance'
  | 'Id';

export const useInvoiceTableColumns = (
  currencyOptions: Intl.NumberFormatOptions,
): TableColumn<BillingTableItem>[] =>
  useMemo(
    () => [
      makeSelectColumn((row) => !invoiceIsPaid(row)),
      {
        Header: 'Status',
        width: 120,
        accessor: invoiceIsPaid,
        sortType: 'basic',
        id: 'Status',
        sortInverted: true,
        sortDescFirst: true,
        Cell: (row: { value: boolean }) => (
          <BooleanCell value={row.value} trueComponent={PaidStatus} falseComponent={UnpaidStatus} />
        ),
        filter: 'equals',
      },
      {
        Header: 'Invoice Number',
        accessor: 'InvoiceNumber',
        minWidth: 180,
      },
      {
        Header: 'Issue Date',
        accessor: 'InvoiceDate',
        Cell: DateCell,
        minWidth: 160,
        filter: dateRange('InvoiceDate'),
      },
      {
        Header: 'Due Date',
        accessor: 'DueDate',
        Cell: DateCell,
        minWidth: 160,
        filter: dateRange('DueDate'),
      },
      {
        Header: `Amount (${currencyOptions.currency})`,
        minWidth: 180,
        accessor: 'Amount',
        Cell: InvoiceAmountCell,
      },
      {
        Header: `Balance (${currencyOptions.currency})`,
        minWidth: 170,
        accessor: 'Balance',
        sortType: 'basic',
        Cell: ({ value }) => <AmountCell value={value} options={currencyOptions} />,
      },
      makeOpenModalColumn('PO Number', {
        accessor: 'PONumber__c',
        width: 155,
        disableSortBy: true,
        Header: 'Add PO Number',
        ModalCell: PONumberModalCell,
      }),
      {
        accessor: 'Id',
        disableSortBy: true,
        width: 50,
        Cell: DownloadCell,
      },
    ],
    [currencyOptions],
  );
